/* Daily devotions — a verse and a thought, at the same time every morning.
 *
 * The client lists these inside bulk messaging ("daily devotion broadcasts"),
 * so they live as a tab on the Messages screen rather than a page of their
 * own. Setting one up is scheduling, not sending: nothing goes out until the
 * time arrives, and each morning it produces an ordinary message that appears
 * in the Sent history like anything else.
 */
import { useState } from 'react'
import { Clock, Loader2, Pause, Play, Sunrise, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Confirm } from '@/components/ui/confirm'
import { Field, SelectField, TextArea } from '@/components/ui/field'
import {
  AttachButtons,
  AttachedFiles,
  AttachmentList,
  useAttachments,
} from './AttachmentPicker'
import {
  useCreateDevotion,
  useDeleteDevotion,
  useDevotions,
  useGroups,
  useSetDevotionActive,
} from '@/api/queries'
import { cn } from '@/lib/utils'
import type { AudienceKind, Devotion } from '@/types/api'

/* Monday is bit 0, matching the server. */
const DAYS = [
  { bit: 1, label: 'M' },
  { bit: 2, label: 'T' },
  { bit: 4, label: 'W' },
  { bit: 8, label: 'T' },
  { bit: 16, label: 'F' },
  { bit: 32, label: 'S' },
  { bit: 64, label: 'S' },
]
const EVERY_DAY = 127

export function DevotionsPanel({ churchId }: { churchId: string }) {
  const devotions = useDevotions(churchId)
  const rows = devotions.data ?? []

  return (
    <div className="space-y-s4">
      <NewDevotion churchId={churchId} />

      {devotions.isLoading ? (
        <Card className="p-s6 text-center text-ink-2">
          <Loader2 className="mx-auto h-5 w-5 animate-spin" aria-label="Loading" />
        </Card>
      ) : null}

      {!devotions.isLoading && rows.length === 0 ? (
        <Card className="p-s8 text-center">
          <Sunrise className="mx-auto h-8 w-8 text-ink-3" aria-hidden />
          <p className="mt-s3 text-md font-bold">No devotions yet</p>
          <p className="mt-1 text-base text-ink-2">
            Set one up and it goes out at the same time every morning, without
            anybody having to remember.
          </p>
        </Card>
      ) : null}

      {rows.map((d) => (
        <DevotionRow key={d.id} devotion={d} churchId={churchId} />
      ))}
    </div>
  )
}

function NewDevotion({ churchId }: { churchId: string }) {
  const groups = useGroups(churchId)
  const create = useCreateDevotion(churchId)
  const attachments = useAttachments()

  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [time, setTime] = useState('07:00')
  const [days, setDays] = useState(EVERY_DAY)
  const [audience, setAudience] = useState<AudienceKind>('church')
  const [groupId, setGroupId] = useState('')
  const [error, setError] = useState<string | null>(null)

  /* Fixed for a devotion, and named here so the file notes say the right
     thing: what is scheduled goes to the app and their phones. */
  const CHANNELS = { in_app: true, push: true, sms: false }

  const needsGroup = audience === 'group'
  const ready =
    title.trim() &&
    body.trim() &&
    days > 0 &&
    (!needsGroup || groupId) &&
    !attachments.isUploading

  if (!open) {
    return (
      <Button variant="secondary" full onClick={() => setOpen(true)}>
        <Sunrise className="h-4 w-4" aria-hidden />
        Set up a daily devotion
      </Button>
    )
  }

  function submit() {
    if (!ready || create.isPending) return
    setError(null)
    create.mutate(
      {
        title: title.trim(),
        body: body.trim(),
        send_time: time,
        days,
        audience_kind: audience,
        group_id: needsGroup ? groupId : null,
        channels: CHANNELS,
        media_ids: attachments.mediaIds,
      },
      {
        onSuccess: () => {
          setTitle('')
          setBody('')
          attachments.reset()
          setOpen(false)
        },
        onError: (err) => setError((err as Error).message),
      },
    )
  }

  return (
    <Card className="space-y-s4 p-s4">
      <Field
        label="Reference"
        value={title}
        onChange={(e) => setTitle(e.target.value.slice(0, 120))}
        placeholder="Psalm 23"
        required
      />

      <TextArea
        label="The devotion"
        value={body}
        onChange={(e) => setBody(e.target.value.slice(0, 2000))}
        placeholder="The verse, and a thought to carry through the day."
        rows={4}
        required
      />

      {/* A VERSE CARD OR A READING TO PRINT. Attached once and sent every
          morning — worth saying plainly, because the natural assumption is
          that a file belongs to one day's devotion. */}
      <div className="space-y-s2">
        <AttachmentList
          files={attachments.files}
          onRemove={attachments.remove}
          channels={CHANNELS}
        />
        {attachments.files.length > 0 ? (
          <p className="text-sm text-ink-2">
            The same file goes out every morning.
          </p>
        ) : null}
        <AttachButtons onPick={attachments.add} disabled={attachments.isFull} />
      </div>

      <div className="grid gap-s3 sm:grid-cols-2">
        <Field
          label="Time"
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          /* The church's own clock, not the reader's. Said out loud because
             a pastor abroad would otherwise reasonably assume theirs. */
          help="Your church's local time"
        />
        <SelectField
          label="Send to"
          value={audience}
          onChange={(e) => {
            setAudience(e.target.value as AudienceKind)
            setGroupId('')
          }}
          options={[
            { value: 'church', label: 'Everyone in the church' },
            { value: 'group', label: 'One group' },
          ]}
        />
      </div>

      {needsGroup ? (
        <SelectField
          label="Group"
          value={groupId}
          onChange={(e) => setGroupId(e.target.value)}
          options={[
            { value: '', label: 'Choose a group' },
            ...(groups.data ?? []).map((g) => ({
              value: g.id,
              label: g.name,
            })),
          ]}
        />
      ) : null}

      <div>
        <span className="mb-s2 block text-sm font-semibold text-ink-2">
          Which days
        </span>
        <div className="flex gap-s1">
          {DAYS.map((d, i) => {
            const on = Boolean(days & d.bit)
            return (
              <button
                key={i}
                type="button"
                aria-pressed={on}
                aria-label={`Day ${i + 1}`}
                onClick={() => setDays((v) => (on ? v & ~d.bit : v | d.bit))}
                className={cn(
                  'h-9 w-9 rounded-pill text-sm font-bold transition-colors',
                  on
                    ? 'bg-brand text-ink-on-brand'
                    : 'bg-surface-2 text-ink-2 hover:bg-hover',
                )}
              >
                {d.label}
              </button>
            )
          })}
        </div>
      </div>

      {error ? (
        <p role="alert" className="rounded-md bg-danger-soft px-s3 py-s2 text-base text-danger-ink">
          {error}
        </p>
      ) : null}

      <div className="flex justify-end gap-s2">
        <Button variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={submit}
          disabled={!ready}
          loading={create.isPending}
        >
          Schedule it
        </Button>
      </div>
    </Card>
  )
}

function DevotionRow({
  devotion,
  churchId,
}: {
  devotion: Devotion
  churchId: string
}) {
  const setActive = useSetDevotionActive(churchId)
  const remove = useDeleteDevotion(churchId)
  const [confirming, setConfirming] = useState(false)

  return (
    <Card className={cn('p-s4', !devotion.is_active && 'opacity-70')}>
      <div className="flex items-start justify-between gap-s3">
        <div className="min-w-0">
          <h3 className="truncate font-semibold">{devotion.title}</h3>
          <p className="mt-0.5 line-clamp-2 text-base text-ink-2">
            {devotion.body}
          </p>
        </div>
        {!devotion.is_active ? (
          <span className="shrink-0 rounded-pill bg-surface-2 px-s3 py-0.5 text-xs font-bold text-ink-2">
            Paused
          </span>
        ) : null}
      </div>

      <AttachedFiles media={devotion.media ?? []} />

      <div className="mt-s3 flex flex-wrap items-center gap-x-s3 gap-y-1 text-sm text-ink-2">
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3.5 w-3.5" aria-hidden />
          {devotion.send_time}
        </span>
        <span aria-hidden>·</span>
        <span>{devotion.days_label}</span>
        <span aria-hidden>·</span>
        <span>{devotion.audience_label}</span>
        {devotion.sent_count > 0 ? (
          <>
            <span aria-hidden>·</span>
            <span>
              {devotion.sent_count} sent so far
            </span>
          </>
        ) : null}
      </div>

      <div className="mt-s3 flex justify-end gap-s2 border-t border-line-2 pt-s3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            setActive.mutate({ id: devotion.id, active: !devotion.is_active })
          }
          loading={setActive.isPending}
        >
          {devotion.is_active ? (
            <>
              <Pause className="h-3.5 w-3.5" aria-hidden />
              Pause
            </>
          ) : (
            <>
              <Play className="h-3.5 w-3.5" aria-hidden />
              Resume
            </>
          )}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setConfirming(true)}
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
          Remove
        </Button>
      </div>

      <Confirm
        open={confirming}
        onOpenChange={setConfirming}
        title="Remove this devotion?"
        /* Says what survives, because "remove" reads as though it takes the
           history with it. */
        body="It will stop going out. What it has already sent stays in your history."
        confirmLabel="Remove"
        onConfirm={() => remove.mutate(devotion.id)}
      />
    </Card>
  )
}
